import throttle from "lodash-es/throttle";
import { useQuery } from "@tanstack/react-query";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { fetchAuthToken } from "app/Auth";
import { LocalStorageKey } from "app/types";
import Constants from "common/constant";
import { client } from "common/apiClient";
import { AccountProfile } from "./types";

/**
 * 保護者プロフィール取得
 */
export const getAccountProfile = createAsyncThunk(
  "account/getAccountProfile",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token }).get(`/v1/user/profile/${uid}`);
    return response.data;
  },
);

/**
 * 保護者プロフィール更新
 */
export const updateAccountProfile = createAsyncThunk(
  "account/updateAccountProfile",
  throttle(
    async ({
      uid,
      profile,
    }: {
      uid: string;
      profile: Partial<AccountProfile>;
    }) => {
      const token = await fetchAuthToken();
      const response = await client({ token }).put(`/v1/user/profile/${uid}`, profile);
      return response.data;
    },
    1000,
  ),
);

// ============================================================
// 親アカウント管理API（user/api.ts から移動）
// ============================================================

export const createParentAccount = async ({
  user,
}: {
  user: {
    uid: string;
    child_uid?: string;
    email: string;
    provider: string;
    display_name: string;
    language: {
      locale: string;
      format: string;
    };
    env: string;
    timezone?: string;
    country?: string;
  };
}) => {
  return client({ token: Constants.apiAuthKey }).post(
    "/v1/user/sign_up/parent",
    user,
  );
};

export const getAccountUsers = createAsyncThunk(
  "user/getAccountUsers",
  async ({ uid }: { uid: string }) => {
    const token = await fetchAuthToken();
    const response = await client({ token: token }).get(
      `/v1/user/account/${uid}`,
    );
    return response.data;
  },
);

export const updateLastSignInTime = (): void => {
  const currentTime = new Date().toISOString();
  localStorage.setItem(LocalStorageKey.LAST_SIGN_AT, currentTime);
};

export const updateAccount = createAsyncThunk(
  "user/updateAccount",
  throttle(
    async ({
      uid,
      userInfo,
    }: {
      uid: string;
      userInfo: {
        icon?: string;
        display_name?: string;
      };
    }) => {
      const token = await fetchAuthToken();
      const response = await client({ token: token }).put(
        `/v1/user/account/parent/${uid}`,
        userInfo,
      );
      return response.data;
    },
    1000,
  ),
);

export const deleteAccount = async ({
  uid,
  options,
  detail,
}: {
  uid: string;
  options: string[];
  detail: string;
}) => {
  const token = await fetchAuthToken();
  return client({ token: token }).delete(`/v1/user/account/parent/${uid}`, {
    data: {
      options,
      detail,
    },
  });
};

export const fetchUserStatus = async ({ uid }: { uid: string }) => {
  const token = await fetchAuthToken();
  return client({ token: token }).get(`/v1/user/status/${uid}`);
};

export const useUserStatus = (uid: string) => {
  return useQuery({
    queryKey: ["user/status", uid],
    queryFn: async () => {
      const response = await fetchUserStatus({ uid });
      return response.data.data;
    },
    enabled: !!uid,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
